import React, { useState, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import {
  exportToCanvas,
  convertToExcalidrawElements,
} from "@excalidraw/excalidraw";
import { buildApiUrl } from "../config";

/**
 * AI tools toolbar for the whiteboard.
 * Props:
 *   excalidrawAPI: ref.current of the <Excalidraw /> component.
 *
 * Features:
 *   - Text -> Diagram (LLM generates skeleton elements)
 *   - Handwriting -> Text (rasterise current selection, OCR via vision LLM)
 *   - Solve Math (rasterise current selection, ask LLM to solve)
 */
const WhiteboardAITools = forwardRef(({ excalidrawAPI, hidden = false }, ref) => {
  const [busy, setBusy] = useState(null); // 'diagram' | 'ocr' | 'math' | 'lesson' | null
  const [error, setError] = useState("");

  // Lesson modal state
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [lessonForm, setLessonForm] = useState({
    topic: "",
    subject: "",
    year_group: "",
    duration_minutes: "60",
  });

  // -------- helpers --------
  const getSelectedElements = () => {
    if (!excalidrawAPI) return [];
    const all = excalidrawAPI.getSceneElements();
    const state = excalidrawAPI.getAppState();
    const selectedIds = state?.selectedElementIds || {};
    return all.filter((el) => selectedIds[el.id]);
  };

  const rasterizeSelection = async () => {
    const selected = getSelectedElements();
    if (selected.length === 0) {
      throw new Error(
        "Select the strokes/shapes you want to convert first (use the selection tool, then drag a box around them)."
      );
    }
    const files = excalidrawAPI.getFiles();
    const canvas = await exportToCanvas({
      elements: selected,
      appState: { exportBackground: true, viewBackgroundColor: "#ffffff" },
      files,
      getDimensions: (w, h) => ({ width: w * 2, height: h * 2, scale: 2 }),
    });
    return canvas.toDataURL("image/png");
  };

  const insertSkeletonElements = (skeletonElements) => {
    // Convert skeleton -> full Excalidraw elements
    const newEls = convertToExcalidrawElements(
      skeletonElements.map((el) => {
        if ((el.type === "rectangle" || el.type === "ellipse" || el.type === "diamond") && el.label) {
          return { ...el, label: { text: String(el.label) } };
        }
        return el;
      })
    );
    const existing = excalidrawAPI.getSceneElements();
    excalidrawAPI.updateScene({ elements: [...existing, ...newEls] });
  };

  const insertTextElement = (text, opts = {}) => {
    const skeleton = [
      {
        type: "text",
        x: opts.x ?? 100,
        y: opts.y ?? 100,
        text,
        fontSize: opts.fontSize ?? 20,
      },
    ];
    const newEls = convertToExcalidrawElements(skeleton);
    const existing = excalidrawAPI.getSceneElements();
    excalidrawAPI.updateScene({ elements: [...existing, ...newEls] });
  };

  // Place new text just below the bounding box of the current selection
  const positionBelowSelection = () => {
    const sel = getSelectedElements();
    if (sel.length === 0) return { x: 100, y: 100 };
    const maxY = Math.max(...sel.map((e) => (e.y || 0) + (e.height || 0)));
    const minX = Math.min(...sel.map((e) => e.x || 0));
    return { x: minX, y: maxY + 24 };
  };

  // -------- handlers --------
  const handleTextToDiagram = async () => {
    setError("");
    const prompt = window.prompt(
      "Describe the diagram you want (e.g. 'flowchart of the water cycle'):"
    );
    if (!prompt) return;
    setBusy("diagram");
    try {
      const res = await axios.post(
        buildApiUrl("/api/whiteboard/ai/text-to-diagram"),
        { prompt }
      );
      const elements = res.data?.elements || [];
      if (elements.length === 0) {
        throw new Error("AI did not return any diagram elements.");
      }
      insertSkeletonElements(elements);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || e.message || "Failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleHandwritingOCR = async () => {
    setError("");
    setBusy("ocr");
    try {
      const dataUrl = await rasterizeSelection();
      const res = await axios.post(
        buildApiUrl("/api/whiteboard/ai/handwriting-ocr"),
        { image_base64: dataUrl }
      );
      const text = (res.data?.text || "").trim();
      if (!text) throw new Error("No text detected.");
      const pos = positionBelowSelection();
      insertTextElement(text, pos);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || e.message || "Failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleSolveMath = async () => {    setError("");
    setBusy("math");
    try {
      const dataUrl = await rasterizeSelection();
      const res = await axios.post(
        buildApiUrl("/api/whiteboard/ai/solve-math"),
        { image_base64: dataUrl }
      );
      const { latex = "", solution = "", steps = [] } = res.data || {};
      const lines = [];
      if (latex) lines.push(`Problem:  ${latex}`);
      if (solution) lines.push(`Answer:   ${solution}`);
      if (Array.isArray(steps) && steps.length) {
        lines.push("Steps:");
        steps.forEach((s, i) => lines.push(`  ${i + 1}. ${s}`));
      }
      if (lines.length === 0) throw new Error("No solution returned.");
      const pos = positionBelowSelection();
      insertTextElement(lines.join("\n"), { ...pos, fontSize: 18 });
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || e.message || "Failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleGenerateLesson = async (e) => {
    e.preventDefault();
    setError("");
    setShowLessonModal(false);
    setBusy("lesson");
    try {
      const res = await axios.post(
        buildApiUrl("/api/whiteboard/ai/generate-lesson"),
        {
          topic: lessonForm.topic,
          subject: lessonForm.subject,
          year_group: lessonForm.year_group,
          duration_minutes: parseInt(lessonForm.duration_minutes, 10) || 60,
        }
      );
      const elements = res.data?.elements || [];
      if (elements.length === 0) throw new Error("AI returned an empty lesson.");
      // Clear the board first, then insert lesson
      excalidrawAPI.updateScene({ elements: [] });
      insertSkeletonElements(elements);
      // Set a sensible scroll-to-fit
      if (excalidrawAPI.scrollToContent) excalidrawAPI.scrollToContent();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || err.message || "Failed to generate lesson.");
    } finally {
      setBusy(null);
    }
  };

  const btn =
    "px-3 py-2 rounded text-white text-sm transition-colors disabled:opacity-50";

  // expose triggers so parent can call from dropdown
  useImperativeHandle(ref, () => ({
    triggerLesson:   () => { setError(""); setShowLessonModal(true); },
    triggerDiagram:  () => handleTextToDiagram(),
    triggerOCR:      () => handleHandwritingOCR(),
    triggerMath:     () => handleSolveMath(),
    isBusy:          () => busy !== null,
  }));

  return (
    <>
      {/* Main AI toolbar — hidden when parent embeds buttons in a dropdown */}
      {!hidden && (
        <div className="flex flex-wrap items-center gap-2 p-2 bg-indigo-50 border-b border-indigo-200">
        <span className="text-xs font-semibold text-indigo-700 mr-1">
          AI tools:
        </span>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => { setError(""); setShowLessonModal(true); }}
          className={`${btn} bg-yellow-600 hover:bg-yellow-700`}
          title="Generate a full structured lesson layout on the board"
        >
          {busy === "lesson" ? "Building…" : "📋 Generate Lesson"}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={handleTextToDiagram}
          className={`${btn} bg-indigo-600 hover:bg-indigo-700`}
          title="Generate a diagram from a text description"
        >
          {busy === "diagram" ? "Generating…" : "✨ Text → Diagram"}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={handleHandwritingOCR}
          className={`${btn} bg-purple-600 hover:bg-purple-700`}
          title="Select handwritten strokes, then click to convert to typed text"
        >
          {busy === "ocr" ? "Reading…" : "✍ Handwriting → Text"}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={handleSolveMath}
          className={`${btn} bg-emerald-600 hover:bg-emerald-700`}
          title="Select a handwritten math problem, then click to solve"
        >
          {busy === "math" ? "Solving…" : "∑ Solve Math"}
        </button>
        {error && (
          <span className="text-xs text-red-600 ml-2 max-w-md truncate" title={error}>
            {error}
          </span>
        )}
      </div>
      )}

      {/* Generate Lesson modal */}
      {showLessonModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">
              📋 Generate Lesson on Board
            </h2>
            <form onSubmit={handleGenerateLesson} className="flex flex-col gap-3">
              <label className="text-sm font-medium text-gray-700">
                Subject *
                <input
                  required
                  className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                  placeholder="e.g. Science, Maths, History"
                  value={lessonForm.subject}
                  onChange={(e) => setLessonForm({ ...lessonForm, subject: e.target.value })}
                />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Topic *
                <input
                  required
                  className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                  placeholder="e.g. The Water Cycle, Fractions, World War 1"
                  value={lessonForm.topic}
                  onChange={(e) => setLessonForm({ ...lessonForm, topic: e.target.value })}
                />
              </label>
              <div className="flex gap-3">
                <label className="flex-1 text-sm font-medium text-gray-700">
                  Year Group
                  <input
                    className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                    placeholder="e.g. Year 7, KS3"
                    value={lessonForm.year_group}
                    onChange={(e) => setLessonForm({ ...lessonForm, year_group: e.target.value })}
                  />
                </label>
                <label className="w-32 text-sm font-medium text-gray-700">
                  Duration (mins)
                  <input
                    type="number"
                    min="20"
                    max="180"
                    className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                    value={lessonForm.duration_minutes}
                    onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })}
                  />
                </label>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                ⚠️ This will replace your current board with the lesson layout.
              </p>
              <div className="flex gap-3 mt-1">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 rounded transition-colors"
                >
                  Generate
                </button>
                <button
                  type="button"
                  onClick={() => setShowLessonModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
});

export default WhiteboardAITools;
